import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { authApi } from '../api/userApi';
import { permissionApiV2 } from '../api/userApiV2';
import {
  clearStoredAuth,
  decodeJwtPayload,
  getStoredAccessToken,
  getStoredUser,
  storeAuthSession
} from '../services/authStorage';
import {
  signInToFirebase,
  signOutFromFirebase,
  waitForFirebaseAuthReady,
} from '../services/firebaseAuthBridge';
import { extractApiErrorMessage } from '../utils/errorHandler';

interface User {
  userId: string;
  userName: string;
  email?: string;
  deptCode?: string;
  deptName?: string;
  dptNo?: string;
  positionId?: string;
  positionName?: string;
  titleCode?: string;
  roles?: string[];
  isActive?: boolean;
  department?: {
    dptName?: string;
  };
  positions?: Array<unknown>;
  permissions?: string[];
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  permissions: string[];
  login: (username: string, password: string, rememberMe?: boolean) => Promise<{ mustChangePassword: boolean }>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refreshPermissions: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState<string[]>([]);

  useEffect(() => {
    checkAuth();
  }, []);

  const clearAuthData = () => {
    clearStoredAuth();
  };

  const loadUserPermissions = async (currentUser?: User | null) => {
    try {
      const response = await permissionApiV2.getCurrentUserPermissions();
      if (response.success && response.data) {
        const effectivePermissions = response.data.effectivePermissions || [];
        const permissionList = effectivePermissions.flatMap((perm) => {
          const entries: string[] = [];
          if (perm.query) entries.push(`${perm.pgCode}_QUERY`);
          if (perm.addNew) entries.push(`${perm.pgCode}_ADD`);
          if (perm.edit) entries.push(`${perm.pgCode}_EDIT`);
          if (perm.delete || perm.del) entries.push(`${perm.pgCode}_DELETE`);
          if (perm.export) entries.push(`${perm.pgCode}_EXPORT`);
          if (perm.print || perm.printer) entries.push(`${perm.pgCode}_PRINT`);
          if (perm.approve) entries.push(`${perm.pgCode}_APPROVE`);
          return entries;
        });

        // v2 effectivePermissions 只有 PG 代碼權限，不含後端在登入時為
        // ADMIN_GROUP 成員合成的「ADMIN」標記 — 從 JWT claims 補回，
        // 否則 usePermission().isAdmin 永遠是 false（僅影響 UI 顯示，
        // 後端 API 各自驗 JWT 內的權限，不受此影響）。
        const token = getStoredAccessToken();
        const claims = token ? decodeJwtPayload(token) : null;
        const jwtPermissions = Array.isArray(claims?.permissions)
          ? (claims!.permissions as unknown[])
          : [];
        if (jwtPermissions.includes('ADMIN') && !permissionList.includes('ADMIN')) {
          permissionList.push('ADMIN');
        }

        setPermissions(permissionList);
        setUser((prevUser) => {
          const nextUser = currentUser ?? prevUser;
          return nextUser ? { ...nextUser, permissions: permissionList } : prevUser;
        });
        return;
      }
    } catch (error) {
      console.error('Failed to load user permissions:', error);
    }

    setPermissions([]);
  };

  const checkAuth = async () => {
    try {
      const token = getStoredAccessToken();
      const savedUser = getStoredUser();

      if (!token || !savedUser) {
        return;
      }

      const storedUser = JSON.parse(savedUser) as User;
      const response = await authApi.verify();
      if (response.status !== 'success') {
        clearAuthData();
        return;
      }

      // Firebase Auth has its own persistent session. On page reload we wait
      // for it to restore; if it didn't (private mode, storage cleared, first
      // visit after this feature shipped), recover by minting a fresh custom
      // token from the backend before exposing the user — otherwise child
      // subscribers would race ahead and hit permission-denied.
      const fbUser = await waitForFirebaseAuthReady();
      if (!fbUser) {
        try {
          const tokenRes = await authApi.firebaseToken();
          if (tokenRes.status === 'success' && tokenRes.data?.firebaseCustomToken) {
            await signInToFirebase(tokenRes.data.firebaseCustomToken);
          }
        } catch (err) {
          console.warn('[AuthContext] Firebase Auth recovery failed; Firestore realtime disabled', err);
        }
      }

      setUser(storedUser);
      await loadUserPermissions(storedUser);
    } catch (error) {
      clearAuthData();
      console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username: string, password: string, rememberMe: boolean = false) => {
    // axios 對 4xx/5xx 直接 reject，不會走到下面的 status 檢查 — 必須在這裡
    // 接住並萃取後端訊息（「帳號不存在」「密碼錯誤」「帳號已被鎖定…」等）。
    // 只包 authApi.login 這一個呼叫：signInToFirebase / loadUserPermissions
    // 的失敗不屬於「登入失敗」，維持原本的錯誤語義。
    const response = await authApi
      .login({
        username,
        password,
        rememberMe
      })
      .catch((err) => {
        if (!err?.response) {
          throw new Error('無法連線到伺服器，請稍後再試');
        }
        throw new Error(extractApiErrorMessage(err, '登入失敗'));
      });

    if (response.status !== 'success' || !response.data) {
      const errorMessage = extractApiErrorMessage(response, '登入失敗');
      throw new Error(errorMessage);
    }

    const {
      token,
      refreshToken,
      user: nextUser,
      mustChangePassword,
      firebaseCustomToken,
    } = response.data;
    storeAuthSession(
      {
        token,
        refreshToken,
        user: nextUser
      },
      rememberMe
    );

    // Sign in to Firebase BEFORE setUser so that downstream subscribers
    // (useFirestoreSync, useInbox) never race against an unauthenticated
    // Firestore connection.
    await signInToFirebase(firebaseCustomToken);

    const normalizedUser = nextUser as unknown as User;
    setUser(normalizedUser);
    await loadUserPermissions(normalizedUser);

    return { mustChangePassword: !!mustChangePassword };
  };

  const logout = async () => {
    try {
      if (getStoredAccessToken()) {
        await authApi.logout();
      }
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      await signOutFromFirebase();
      clearAuthData();
      setUser(null);
      setPermissions([]);
      window.location.href = '/login';
    }
  };

  const isAuthenticated = !!user;

  const refreshPermissions = async () => {
    await loadUserPermissions();
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated,
      permissions,
      login,
      logout,
      checkAuth,
      refreshPermissions
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
